import { CommonProp } from '@acin/ui-core';
import React from 'react';
import { useParams } from 'react-router-dom';
import { IInternalRegulation, IInternalRegulationDocumentInstance } from '../../../graphql/generated';
import { REGULATION_TABS } from '../../../helpers/utils';

interface IContext {
  activeRegulation: IInternalRegulation;
  onUpdateActiveRegulation: (regulation: IInternalRegulation) => void;
  showDocumentModal: boolean;
  onUpdateShowDocumentModal: (show: boolean) => void;
  activeDocument?: IInternalRegulationDocumentInstance;
  onUpdateActiveDocument: (document?: IInternalRegulationDocumentInstance) => void;
  documentsInitialised: boolean;
  onUpdateDocumentsInitialised: (state: boolean) => void;
  activeRegulationTabId: string;
  onUpdateActiveRegulationTabId: (tabId: string) => void;
}

export const INITIAL_STATE: IContext = {
  activeRegulation: {} as IInternalRegulation,
  onUpdateActiveRegulation: () => {},
  activeDocument: undefined,
  onUpdateActiveDocument: () => {},
  showDocumentModal: false,
  onUpdateShowDocumentModal: (show: boolean) => {},
  documentsInitialised: false,
  onUpdateDocumentsInitialised: (state: boolean) => {},
  activeRegulationTabId: REGULATION_TABS.REVIEW.id,
  onUpdateActiveRegulationTabId: () => {},
};

export const Context = React.createContext(INITIAL_STATE);

export const useRegulation = () => React.useContext(Context);

export const RegulationProvider: React.FC<CommonProp> = ({ children }) => {
  const [activeRegulation, setActiveRegulation] = React.useState<IInternalRegulation>({} as IInternalRegulation);
  const [activeDocument, setActiveDocument] = React.useState<IInternalRegulationDocumentInstance | undefined>(
    undefined,
  );
  const [showDocumentModal, setShowDocumentModal] = React.useState<boolean>(false);
  const [documentsInitialised, setDocumentsInitialised] = React.useState(false);

  const { tabId } = useParams<{ regulationId: string; tabId: string }>();
  const activeRegulationTabId = tabId ?? REGULATION_TABS.REVIEW.id;

  const onUpdateActiveRegulation = React.useCallback(
    (regulation: IInternalRegulation) => {
      regulation && setActiveRegulation(regulation);
    },
    [setActiveRegulation],
  );

  const onUpdateActiveDocument = React.useCallback(
    (document?: IInternalRegulationDocumentInstance) => {
      setActiveDocument(document);
    },
    [setActiveDocument],
  );

  const onUpdateShowDocumentModal = React.useCallback(
    (show: boolean) => {
      setShowDocumentModal(show);
    },
    [setShowDocumentModal],
  );

  const onUpdateDocumentsInitialised = React.useCallback(
    (state: boolean) => {
      setDocumentsInitialised(state);
    },
    [setDocumentsInitialised],
  );

  return (
    <Context.Provider
      value={{
        ...INITIAL_STATE,
        activeRegulation,
        activeDocument,
        showDocumentModal,
        documentsInitialised,
        activeRegulationTabId,
        onUpdateActiveRegulation,
        onUpdateActiveDocument,
        onUpdateShowDocumentModal,
        onUpdateDocumentsInitialised,
      }}
    >
      {children}
    </Context.Provider>
  );
};
